import React, {
  createContext,
  useContext,
  useReducer,
  useCallback,
  ReactNode,
} from "react";
import { getBadges } from "../services/badgeService";
import { getCommitmentScores } from "../services/webapi";
import { useUserContext } from "./UserContext";

// Types
export interface ScoreData {
  reliabilityScore: number;
  punctualityScore: number;
  confidenceScore: number;
  totalPoints: number;
  completedCommitments: number;
  onTimeCommitments: number;
  totalCommitments: number;
}

export interface UserScore {
  reliabilityScore: number;
  punctualityScore: number;
  confidenceScore: number;
  madeTotal: number;
}

export interface Commitment {
  id: string | number;
  title: string;
  description: string;
  dueDate: string;
  committedDate: string;
  assignee: string;
  status:
    | "pending"
    | "approved"
    | "rejected"
    | "question"
    | "pending_approval"
    | "pending-acceptance"
    | "initiated"
    | "accepted"
    | "declined";
  type?: "nudge";
  nudgesLeft?: number;
  totalNudges?: number;
  questions?: string[];
  explanation?: string;
  isExternal?: boolean;
  reliabilityPoint?: number;
  badge?: { id: string; _id?: string; name?: string; [key: string]: any };
  completedDate?: string;
  committer?: { _id: string; [key: string]: any };
}

export interface ScoreState {
  scores: ScoreData | null;
  userScores: UserScore[];
  loading: boolean;
  error: string | null;
  lastCalculated: number | null;
}

// Action types
type ScoreAction =
  | { type: "CALCULATE_SCORES_START" }
  | { type: "CALCULATE_SCORES_SUCCESS"; payload: ScoreData }
  | { type: "CALCULATE_SCORES_ERROR"; payload: string }
  | { type: "CALCULATE_TOTAL_POINTS_START" }
  | { type: "CALCULATE_TOTAL_POINTS_SUCCESS"; payload: number }
  | { type: "CALCULATE_TOTAL_POINTS_ERROR"; payload: string }
  | { type: "FETCH_USER_SCORES_START" }
  | { type: "FETCH_USER_SCORES_SUCCESS"; payload: UserScore[] }
  | { type: "FETCH_USER_SCORES_ERROR"; payload: string };

// Initial state
const initialState: ScoreState = {
  scores: null,
  userScores: [],
  loading: false,
  error: null,
  lastCalculated: null,
};

// Reducer
const scoreReducer = (state: ScoreState, action: ScoreAction): ScoreState => {
  switch (action.type) {
    case "CALCULATE_SCORES_START":
      return {
        ...state,
        loading: true,
        error: null,
      };
    case "CALCULATE_SCORES_SUCCESS":
      return {
        ...state,
        loading: false,
        scores: action.payload,
        lastCalculated: Date.now(),
        error: null,
      };
    case "CALCULATE_SCORES_ERROR":
      return {
        ...state,
        loading: false,
        error: action.payload,
      };
    case "CALCULATE_TOTAL_POINTS_START":
      return {
        ...state,
        loading: true,
        error: null,
      };
    case "CALCULATE_TOTAL_POINTS_SUCCESS":
      return {
        ...state,
        loading: false,
        scores: state.scores
          ? { ...state.scores, totalPoints: action.payload }
          : null,
        lastCalculated: Date.now(),
        error: null,
      };
    case "CALCULATE_TOTAL_POINTS_ERROR":
      return {
        ...state,
        loading: false,
        error: action.payload,
      };
    case "FETCH_USER_SCORES_START":
      return {
        ...state,
        loading: true,
        error: null,
      };
    case "FETCH_USER_SCORES_SUCCESS":
      return {
        ...state,
        loading: false,
        userScores: action.payload,
        error: null,
      };
    case "FETCH_USER_SCORES_ERROR":
      return {
        ...state,
        loading: false,
        error: action.payload,
      };
    default:
      return state;
  }
};

// Context
interface ScoreContextType {
  scores: ScoreData | null;
  userScores: UserScore[];
  loading: boolean;
  error: string | null;
  lastCalculated: number | null;
  calculateScores: (commitments: Commitment[]) => void;
  calculateTotalPoints: (commitments: Commitment[]) => Promise<void>;
  calculateScoresForCommitments: (
    commitments: Commitment[],
    opts?: { includeBadgeAsync?: boolean }
  ) => void;
  fetchUserScores: () => Promise<void>;
}

const ScoreContext = createContext<ScoreContextType | undefined>(undefined);

// Provider component
interface ScoreProviderProps {
  children: ReactNode;
}

export const ScoreProvider: React.FC<ScoreProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(scoreReducer, initialState);
  const { getUserId, isAuthenticated } = useUserContext();

  // Helper functions
  const calculateReliabilityScore = useCallback(
    (commitments: Commitment[]): number => {
      const completedCommitments = commitments.filter(
        (c) => c.status === "approved"
      );
      if (completedCommitments.length === 0) return 0;

      const totalReliabilityPoints = completedCommitments.reduce(
        (acc, c) => acc + (c.reliabilityPoint || 0),
        0
      );

      return (
        Math.round(
          (totalReliabilityPoints / completedCommitments.length) * 100
        ) / 100
      );
    },
    []
  );

  const calculatePunctualityScore = useCallback(
    (commitments: Commitment[]): number => {
      const completedCommitments = commitments.filter(
        (c) => c.status === "approved"
      );
      if (completedCommitments.length === 0) return 0;

      const onTimeCommitments = completedCommitments.filter((c) => {
        if (
          c.completedDate === undefined ||
          c.completedDate === " " ||
          c.completedDate === null ||
          c.dueDate === undefined ||
          c.dueDate === " " ||
          c.dueDate === null
        ) {
          return true;
        }
        return c.dueDate >= c.completedDate;
      });

      return Math.round(
        (onTimeCommitments.length / completedCommitments.length) * 100
      );
    },
    []
  );

  const calculateConfidenceScore = useCallback(
    (_commitments: Commitment[]): number => {
      // Simplified confidence calculation - can be enhanced later
      return 0;
    },
    []
  );

  // Main calculation function
  const calculateScores = useCallback(
    (commitments: Commitment[]) => {
      dispatch({ type: "CALCULATE_SCORES_START" });

      try {
        const reliabilityScore = calculateReliabilityScore(commitments);
        const punctualityScore = calculatePunctualityScore(commitments);
        const confidenceScore = calculateConfidenceScore(commitments);

        const completedCommitments = commitments.filter(
          (c) => c.status === "approved"
        ).length;
        const onTimeCommitments = commitments.filter((c) => {
          if (c.status !== "approved") return false;
          if (
            c.completedDate === undefined ||
            c.completedDate === " " ||
            c.completedDate === null ||
            c.dueDate === undefined ||
            c.dueDate === " " ||
            c.dueDate === null
          ) {
            return true;
          }
          return c.dueDate >= c.completedDate;
        }).length;

        const scores: ScoreData = {
          reliabilityScore,
          punctualityScore,
          confidenceScore,
          totalPoints: 0, // Will be calculated separately
          completedCommitments,
          onTimeCommitments,
          totalCommitments: commitments.length,
        };

        dispatch({ type: "CALCULATE_SCORES_SUCCESS", payload: scores });
      } catch (error) {
        dispatch({
          type: "CALCULATE_SCORES_ERROR",
          payload: error instanceof Error ? error.message : "Unknown error",
        });
      }
    },
    [
      calculateReliabilityScore,
      calculatePunctualityScore,
      calculateConfidenceScore,
    ]
  );

  // Async total points calculation
  const calculateTotalPoints = useCallback(
    async (commitments: Commitment[]) => {
      dispatch({ type: "CALCULATE_TOTAL_POINTS_START" });

      try {
        if (!isAuthenticated) {
          dispatch({ type: "CALCULATE_TOTAL_POINTS_SUCCESS", payload: 0 });
          return;
        }

        // Filter user commitments
        const userId = getUserId();
        let user_commitments: Commitment[] = [];
        for (const commitment of commitments) {
          if (commitment?.committer?._id === userId) {
            user_commitments.push(commitment);
          }
        }

        const fulfilledData = user_commitments.filter(
          (c) => c.status === "approved"
        );
        const pendingData = user_commitments.filter(
          (c) =>
            ![
              "approved",
              "rejected",
              "pending-acceptance",
              "initiated",
              "accepted",
              "declined",
            ].includes(c.status)
        );
        const rejectedData = user_commitments.filter(
          (c) => c.status === "rejected"
        );

        // Fetch badges (for future badge-related calculations)
        try {
          await getBadges();
        } catch {
          // Badge fetch failed, continue without badge data
        }

        let pointBalance = 0;

        // Base points
        if (fulfilledData.length || pendingData.length || rejectedData.length) {
          pointBalance += 10;
        }

        // Commitment points
        pointBalance +=
          fulfilledData.length + pendingData.length + rejectedData.length;
        pointBalance += fulfilledData.length * 10;

        // On-time bonus
        const ontimeNum = fulfilledData.filter((c) => {
          if (
            c.completedDate === undefined ||
            c.completedDate === " " ||
            c.completedDate === null ||
            c.dueDate === undefined ||
            c.dueDate === " " ||
            c.dueDate === null
          ) {
            return true;
          }
          return c.dueDate >= c.completedDate;
        }).length;
        pointBalance += ontimeNum * 10;

        // Reliability points
        const Reliability90Point = fulfilledData.reduce(
          (acc, c) => acc + (c.reliabilityPoint || 0),
          0
        );
        pointBalance += Reliability90Point;

        dispatch({
          type: "CALCULATE_TOTAL_POINTS_SUCCESS",
          payload: pointBalance,
        });
      } catch (error) {
        dispatch({
          type: "CALCULATE_TOTAL_POINTS_ERROR",
          payload: error instanceof Error ? error.message : "Unknown error",
        });
      }
    },
    [isAuthenticated, getUserId]
  );

  // Combined function
  const calculateScoresForCommitments = useCallback(
    (commitments: Commitment[], opts?: { includeBadgeAsync?: boolean }) => {
      calculateScores([...commitments]);

      if (opts?.includeBadgeAsync) {
        calculateTotalPoints(commitments);
      }
    },
    [calculateScores, calculateTotalPoints]
  );

  // Fetch user scores from API
  const fetchUserScores = useCallback(async () => {
    if (!isAuthenticated) {
      return;
    }

    dispatch({ type: "FETCH_USER_SCORES_START" });
    try {
      const userId = getUserId();
      if (!userId) {
        throw new Error("User not authenticated");
      }

      const committerIds = [userId];
      const badgeIds: string[] = [];

      const badgeResponse = await getBadges();
      const responseBadges = badgeResponse.data.badges.map((badge: any) => ({
        value: badge._id,
        label: badge.name,
      }));

      for (const badge of responseBadges) {
        badgeIds.push(badge.value);
      }

      const scoresResponse = await getCommitmentScores(
        committerIds,
        badgeIds,
        "",
        "current"
      );

      dispatch({
        type: "FETCH_USER_SCORES_SUCCESS",
        payload: scoresResponse.data,
      });
    } catch (error) {
      dispatch({
        type: "FETCH_USER_SCORES_ERROR",
        payload:
          error instanceof Error
            ? error.message
            : "Failed to fetch user scores",
      });
    }
  }, [isAuthenticated, getUserId]);

  const value: ScoreContextType = {
    scores: state.scores,
    userScores: state.userScores,
    loading: state.loading,
    error: state.error,
    lastCalculated: state.lastCalculated,
    calculateScores,
    calculateTotalPoints,
    calculateScoresForCommitments,
    fetchUserScores,
  };

  return (
    <ScoreContext.Provider value={value}>{children}</ScoreContext.Provider>
  );
};

// Hook to use the context
export const useScoreContext = (): ScoreContextType => {
  const context = useContext(ScoreContext);
  if (context === undefined) {
    throw new Error("useScoreContext must be used within a ScoreProvider");
  }
  return context;
};
